import type { ContentBlock, PageContent } from "@/lib/content-source";

/**
 * Profile data is intentionally a read model: legacy captures/CMS content provide editorial facts,
 * while Strapi listing IDs remain the separate purchasable inventory model.  The stable `entityKey`
 * can later be mapped to a vendor-managed Strapi document without changing public URLs or accounts.
 */
export type PublicProfileKind =
  | "mountain"
  | "rental-vendor"
  | "activity-vendor"
  | "vendor"
  | "member";
export interface PublicProfile {
  entityKey: string;
  kind: PublicProfileKind;
  name: string;
  parent?: { kind: "mountain"; key: string; name: string };
  about: string[];
  contact: { phone?: string; email?: string };
  reviews: string[];
  sections: { title: string; body: string[] }[];
  source: "captured-content" | "cms-adapter";
}

/**
 * WHAT THE HOST SURFACE ALREADY PRESENTS — the contract that keeps a fact on the page once.
 *
 * The read model above is complete (name, about, contact, reviews, sections) so a crawler, a no-JS
 * visitor or a bare template can render a whole profile from it. But every surface that mounts
 * `PublicProfileSections` today already presents most of it on its own: the design export's
 * directory-detail screen renders the entity's name, About body, special note, contact rows and
 * PRODUCTS; the componentized MountainProfile renders the body and the MOUNTAIN DETAILS table; the
 * marketplace mirror renders the captured ProfileCenter. Measured in the browser on staging 3757cbd0
 * (PR #66): mounting the full block under the design put the vendor's About copy and contact details
 * on /mountain-whistler/activities/adventure-group and /mountain-whistler/equipment-rentals/black-tie
 * TWICE — the designed profile, then a second generic one.
 *
 * So a mount declares what its host presents and the component renders only the COMPLEMENT. The
 * hierarchy line and Reviews (with their empty states) are the fields no host carries, so they always
 * render. Declaring a fact "presented" that the host does not render would drop it from the page, so
 * each constant names the surface it was measured against — a new mount must measure its own.
 *
 * The export-served routes (the design's `dIsProfile` screen) mount NOTHING: the founder's QA batch of
 * 2026-09-17 (item 1) rules the designed profile the single profile surface there, so the shared
 * block is not appended — not even as a complement.
 */
export type ProfileHostPresents = Readonly<{
  /** The host renders the About body (the same paragraphs `about` holds). */
  about?: boolean;
  /** The host renders the contact facts (email / phone rows). */
  contact?: boolean;
  /** The host renders the record's own sections (special note, facts table). */
  sections?: boolean;
}>;

export const HOST_PRESENTS = {
  /** A bare mount: nothing else on the page carries the profile, so every block renders. */
  nothing: {} as ProfileHostPresents,
  /** The marketplace mirror's captured ProfileCenter: the supplier's description, "Special Note:" with
   *  Email / Phone, and the product rows. */
  mirrorDetail: { about: true, contact: true, sections: true } as ProfileHostPresents,
  /** The componentized MountainProfile template: the body under its H1 and the MOUNTAIN DETAILS fact
   *  table. It renders no contact block, so the contact empty state stays with the complement. */
  mountainTemplate: { about: true, sections: true } as ProfileHostPresents,
} as const;

export interface ProfileBlocks {
  hierarchy: boolean;
  about: boolean;
  contact: boolean;
  reviews: boolean;
  sections: boolean;
}

/** The blocks a mount renders: everything the read model holds, minus what the host presents. */
export function profileBlocks(
  profile: PublicProfile,
  host: ProfileHostPresents = HOST_PRESENTS.nothing,
): ProfileBlocks {
  return {
    hierarchy: !!profile.parent,
    about: !host.about && profile.about.length > 0,
    contact: !host.contact,
    reviews: true,
    sections: !host.sections && profile.sections.length > 0,
  };
}

const textOf = (block: ContentBlock) =>
  "text" in block ? block.text.trim() : "";

/** The mountain kind reads the profile data file (the cms-adapter source), not a capture. One
 *  definition, used by both the componentized MountainProfile and the export-served profile route. */
export function profileFromMountainData(
  code: string,
  data: {
    dName: string;
    dBody: { text: string }[];
    dFacts: { label: string; value: string }[];
    dFactsTitle: string;
  },
): PublicProfile {
  return {
    entityKey: `mountain-${code}`,
    kind: "mountain",
    name: data.dName,
    about: data.dBody.map((p) => p.text),
    contact: {},
    reviews: [],
    sections: data.dFacts.length
      ? [{ title: data.dFactsTitle, body: data.dFacts.map((fact) => `${fact.label}: ${fact.value}`) }]
      : [],
    source: "cms-adapter",
  };
}

export function profileFromContent(input: {
  slug: string;
  kind: PublicProfileKind;
  name: string;
  mountain?: { key: string; name: string };
  content: PageContent | null;
}): PublicProfile {
  // The profile begins at its own H1. What precedes it on a captured page is site chrome — the
  // research-category rail ("Select research category…", "Show All", the sibling vendor names) —
  // and it was rendering as the About text on the served route (measured: black-tie's About led
  // with "Black Tie Ski Rentals" / "Evolution Whistler", the rail, not the description under its H2).
  const all = input.content?.blocks ?? [];
  const h1 = all.findIndex((block) => block.type === "heading" && block.level === 1);
  const fromH1 = h1 >= 0 ? all.slice(h1) : all;
  // And it ends where the purchasable surface begins: the first list item is the product-type tab
  // rail ("SKI" / "BOARD", "dogsled" / "snowshoe"), followed by the product cards. Those are the
  // store's rows, not the profile's — measured: they were rendering under "Special Note:".
  const store = fromH1.findIndex((block) => block.type === "listitem");
  const blocks = store >= 0 ? fromH1.slice(0, store) : fromH1;
  const paragraphs = blocks
    .filter((block) => block.type === "paragraph")
    .map(textOf)
    .filter(Boolean);
  const headingIndexes = blocks
    .map((block, index) => ({ block, index }))
    .filter(({ block }) => block.type === "heading" && "text" in block);
  const contact: PublicProfile["contact"] = {};
  const body: string[] = [];
  const reviews: string[] = [];
  const sections: PublicProfile["sections"] = [];
  // A line that yielded a contact fact is surfaced ONCE, under Contact — never repeated inside the
  // capture's own "Special Note:" section (measured: that section re-listed Email/Phone verbatim).
  const contactLines = new Set<string>();

  for (const paragraph of paragraphs) {
    const email = /\b(?:email|e-mail)\s*:\s*([^\s,;]+)/i.exec(paragraph)?.[1];
    const phone = /\b(?:phone|tel(?:ephone)?)\s*:\s*([^\n]+)/i
      .exec(paragraph)?.[1]
      ?.trim();
    if (email) contact.email = email;
    if (phone) contact.phone = phone;
    if (email || phone) contactLines.add(paragraph);
    else body.push(paragraph);
  }

  for (const { block, index } of headingIndexes) {
    const title = textOf(block);
    const nextHeading =
      headingIndexes.find(({ index: candidate }) => candidate > index)?.index ??
      blocks.length;
    const sectionBody = blocks
      .slice(index + 1, nextHeading)
      .map(textOf)
      .filter((line) => Boolean(line) && !contactLines.has(line));
    if (/review/i.test(title)) reviews.push(...sectionBody);
    else if (
      sectionBody.length &&
      title.localeCompare(input.name, undefined, { sensitivity: "accent" }) !== 0
    )
      sections.push({ title, body: sectionBody });
  }

  const nonAboutContent = new Set([
    ...reviews,
    ...sections.flatMap((section) => section.body),
  ]);

  return {
    entityKey: input.slug,
    kind: input.kind,
    name: input.name,
    ...(input.mountain
      ? { parent: { kind: "mountain", ...input.mountain } }
      : {}),
    about: body.filter(
      (paragraph) =>
        !nonAboutContent.has(paragraph) &&
        !/^(show all|select research category)/i.test(paragraph),
    ),
    contact,
    reviews,
    sections,
    source: "captured-content",
  };
}
